/**
 * Shared PSD color conversion helpers. Centralized so the four
 * PSD import / export modules don't each duplicate identical
 * hex ↔ ag-psd Color logic.
 *
 * (Pre-PR review R1 — audits flagged the duplicated
 * `psdColorToHex` / `hexToPsdColor` helpers across
 * psd-vector-import + psd-text-import + psd-vector-export +
 * psd-text-export as a maintenance trap.)
 */

import type { Color } from 'ag-psd';

/** Convert ag-psd's `Color` discriminated union to a 6-digit hex
 *  string. ag-psd's Color shape varies by color mode (RGB / CMYK
 *  / Lab / Grayscale / etc.); we only honor RGB natively and
 *  fall back to black on other modes. The PSD stash preserves
 *  the original color chunks for export-side fidelity. */
export function psdColorToHex(color: Color | undefined): string {
  if (!color) return '#000000';
  // RGB Color uses `{ r, g, b }` in 0..255.
  const rgb = color as { r?: number; g?: number; b?: number };
  if (typeof rgb.r === 'number' && typeof rgb.g === 'number' && typeof rgb.b === 'number') {
    const toHex = (n: number) =>
      Math.round(Math.max(0, Math.min(255, n)))
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
  }
  return '#000000';
}

/** Convert a hex / 3-digit / 8-digit hex CSS color → ag-psd RGB
 *  `Color`. Hex with alpha (#rrggbbaa) drops the alpha (PSD color
 *  chunks are opaque). Unknown formats default to black; the
 *  caller is responsible for any validation upstream. */
export function hexToPsdColor(color: string): Color {
  const trimmed = color.trim();
  // 6-digit hex
  const hex6 = /^#([0-9a-fA-F]{6})$/.exec(trimmed);
  if (hex6) {
    return {
      r: parseInt(hex6[1].slice(0, 2), 16),
      g: parseInt(hex6[1].slice(2, 4), 16),
      b: parseInt(hex6[1].slice(4, 6), 16),
    } as Color;
  }
  // 3-digit hex — expand each digit
  const hex3 = /^#([0-9a-fA-F]{3})$/.exec(trimmed);
  if (hex3) {
    const expand = (c: string) => parseInt(c + c, 16);
    return {
      r: expand(hex3[1][0]),
      g: expand(hex3[1][1]),
      b: expand(hex3[1][2]),
    } as Color;
  }
  // 8-digit hex with alpha — drop the alpha component
  const hex8 = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})$/.exec(trimmed);
  if (hex8) {
    return {
      r: parseInt(hex8[1].slice(0, 2), 16),
      g: parseInt(hex8[1].slice(2, 4), 16),
      b: parseInt(hex8[1].slice(4, 6), 16),
    } as Color;
  }
  return { r: 0, g: 0, b: 0 } as Color;
}
